import { CheckCircle, Download, Image as LucideImage, Scissors } from 'lucide-react'
import Image from 'next/image'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  nativeBackgroundRemoval,
  type NativeBackgroundRemovalResult,
} from 'sprites-types/workflow/tools/nativeBackgroundRemoval'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function NativeBackgroundRemovalDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(nativeBackgroundRemoval, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as NativeBackgroundRemovalResult
  const inputs = nodeExecution.inputs

  const processedImageUrl = result.source.value || ''
  const originalImageUrl = inputs.imageUrl || inputs.url || inputs.image || ''
  const filename = result.filename || 'background-removed.png'
  const format = 'PNG'
  const size = undefined

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return ''
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <ContextMenuWrapper actions={[]}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="px-4 py-3">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex-shrink-0">
              <div className="rounded-full bg-green-100 p-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-center gap-2">
                <Scissors className="h-4 w-4 text-green-600" />
                <div className="text-sm font-semibold text-gray-900">Background Removed</div>
              </div>

              <div className="mb-3 grid grid-cols-2 gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <LucideImage className="h-3 w-3" />
                  <span>{format} format</span>
                </div>
                {size && (
                  <div>
                    <span>
                      Size: {formatFileSize(typeof size === 'string' ? parseInt(size) : size)}
                    </span>
                  </div>
                )}
              </div>

              {processedImageUrl && (
                <div className="mb-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {originalImageUrl && (
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-700">Original:</div>
                        <Image
                          src={originalImageUrl}
                          alt="Original image"
                          width={400}
                          height={128}
                          className="h-32 w-full rounded-2xl bg-gray-100 object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <div className="mb-1 text-xs font-medium text-gray-700">Processed:</div>
                      <Image
                        src={processedImageUrl}
                        alt="Background removed"
                        width={400}
                        height={128}
                        className="h-32 w-full rounded-2xl bg-gray-100 object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                {processedImageUrl && (
                  <a
                    href={processedImageUrl}
                    download={filename}
                    className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800"
                  >
                    <Download className="h-3 w-3" />
                    Download Image
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadNativeBackgroundRemoval(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function nativeBackgroundRemovalToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(nativeBackgroundRemoval, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as NativeBackgroundRemovalResult
  const inputs = nodeExecution.inputs

  const processedImageUrl = result.source.value || ''
  const originalImageUrl = inputs.imageUrl || inputs.url || inputs.image || ''
  const filename = result.filename || 'background-removed.png'

  return `## Background Removal Complete

**Original Image:** ${originalImageUrl || 'N/A'}
**Processed Image:** ${processedImageUrl || 'N/A'}
**Filename:** ${filename}

The background has been successfully removed from the image.

---
`
}

export const NativeBackgroundRemovalDisplay = createWorkflowToolComponent(
  NativeBackgroundRemovalDisplayComponent,
  downloadNativeBackgroundRemoval,
  nativeBackgroundRemovalToMarkdown
)
